import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Upload,
  FolderPlus,
  RefreshCw,
  Search,
  FileText,
  FileSpreadsheet,
  FileImage,
  File,
  MoreVertical,
  Download,
  Edit3,
  Trash2,
  History,
  Eye,
  Users,
  User,
  Briefcase,
  Shield,
  X,
  Folder,
  Sparkles,
  Loader2,
  FolderOpen,
  ChevronRight,
  Move,
  Library,
  Home,
  ArrowLeft,
  AlertTriangle,
  Calendar,
  Grid3X3,
  List,
  Info
} from 'lucide-react';
import api from '../utils/api';
import FileUploadModal from '../components/Files/FileUploadModal';
import FilePreviewModal from '../components/Files/FilePreviewModal';
import FileEditModal from '../components/Files/FileEditModal';
import CreateFolderModal from '../components/Files/CreateFolderModal';
import FileMoveModal from '../components/Files/FileMoveModal';
import AddToCollectionModal from '../components/Files/AddToCollectionModal';
import FileHistorySidebar from '../components/Files/FileHistorySidebar';
import ComplianceDetailsModal from '../components/Files/ComplianceDetailsModal';
import AdminDocumentsAIPanel from '../components/Files/AdminDocumentsAIPanel';

// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
//  DESIGN TOKENS â€” Drive-style UI
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
const TOKENS = {
  textPrimary: 'var(--foreground)',
  textSecondary: 'var(--muted-foreground)',
  accent: 'var(--accent)',
  accentHover: 'var(--accent-light)',
  border: 'var(--border)',
  bgPage: 'var(--background)',
  bgCard: 'var(--card)',
  bgHover: 'var(--secondary)',
  cardRadius: '8px',
  cardPadding: '16px',
  btnHeight: '36px',
  btnRadius: '6px',
  btnPadding: '8px 16px',
  filterHeight: '36px',
  filterRadius: '6px',
  filterPadding: '8px 12px',
  // File type colors
  pdfColor: '#EA4335',
  spreadsheetColor: '#34A853',
  imageColor: '#4285F4',
  docColor: '#4285F4',
  folderColor: '#5F6368',
  defaultFileColor: '#5F6368',
};


const AdminFilesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [moveMode, setMoveMode] = useState('move');
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [collectionFile, setCollectionFile] = useState(null);
  const [processingAiFileIds, setProcessingAiFileIds] = useState(new Set());
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'


  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [currentFolderPhase, setCurrentFolderPhase] = useState(null);

  // Filters - initialize from URL params
  const [searchQuery, setSearchQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [reviewStatusFilter, setReviewStatusFilter] = useState(searchParams.get('review_status') || 'all');

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchFiles(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // History & Compliance
  const [showHistorySidebar, setShowHistorySidebar] = useState(false);
  const [historyFile, setHistoryFile] = useState(null);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [complianceFile, setComplianceFile] = useState(null);

  // Dropdown state
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openFolderMenuId, setOpenFolderMenuId] = useState(null);

  // Drag-and-drop state
  const [draggedFile, setDraggedFile] = useState(null);
  const [dropTargetFolderId, setDropTargetFolderId] = useState(null);
  const [isDraggingExternal, setIsDraggingExternal] = useState(false);

  // Calculate file counts from loaded files (only valid for current page/view)
  // For global stats, we might need a separate endpoint or stick to page-level stats
  const fileCounts = useMemo(() => {
    // This now only reflects loaded files, might want to remove if confusing
    const counts = { all: 0, preboarding: 0, onboarding: 0, both: 0 };
    return counts;
  }, [files]);

  // Lifecycle filter options
  const lifecycleOptions = [
    { id: 'all', label: 'All' },
    { id: 'preboarding', label: 'Preboarding' },
    { id: 'onboarding', label: 'Onboarding' },
    { id: 'both', label: 'Both' },
  ];

  // Audience filter options
  const audienceOptions = [
    { id: 'all', label: 'All Audiences' },
    { id: 'company-wide', label: 'Everyone' },
    { id: 'role-specific', label: 'By Role' },
    { id: 'employee-specific', label: 'Individual' },
    { id: 'private', label: 'Admin Only' },
  ];

  // Review status filter options
  const reviewStatusOptions = [
    { id: 'all', label: 'All Documents' },
    { id: 'needs_review', label: 'Needs Review' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'approaching', label: 'Due Soon' },
  ];

  // Initialize review status from URL params
  useEffect(() => {
    const urlReviewStatus = searchParams.get('review_status');
    if (urlReviewStatus && urlReviewStatus !== reviewStatusFilter) {
      setReviewStatusFilter(urlReviewStatus);
    }
  }, [searchParams]);

  useEffect(() => {
    // Reset page to 1 on filter change
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData(1);
  }, [audienceFilter, currentFolderId, reviewStatusFilter, lifecycleFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if ((openMenuId || openFolderMenuId) && !event.target.closest('.dropdown-menu')) {
        setOpenMenuId(null);
        setOpenFolderMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId, openFolderMenuId]);

  const fetchData = async (pageNum = 1) => {
    try {
      setLoading(true);
      await Promise.all([fetchFiles(pageNum), fetchFolders()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async (pageNum = pagination.page) => {
    try {
      const token = localStorage.getItem('token');
      const params = {
        page: pageNum,
        limit: pagination.limit
      };

      // Search
      if (searchQuery.trim()) {
        params.search = searchQuery;
      }

      // Apply common filters
      if (lifecycleFilter !== 'all') {
        params.lifecycle_stage = lifecycleFilter;
      }
      if (audienceFilter !== 'all') {
        params.audience = audienceFilter;
      }
      if (reviewStatusFilter !== 'all') {
        params.review_status = reviewStatusFilter;
      }

      // If we're inside a folder, fetch files for that folder
      // At root level, only show files NOT in any folder (drive-style)
      if (currentFolderId) {
        params.folder_id = currentFolderId;
      } else if (!searchQuery.trim()) {
        params.folder_id = 'root';
      }

      const response = await api.get('/files/company/unified', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data.success) {
        setFiles(response.data.files);
        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page,
            limit: response.data.pagination.limit,
            total: response.data.pagination.total,
            pages: response.data.pagination.pages
          });
        }
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to fetch documents');
    }
  };

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = { parent_id: currentFolderId || 'null' };

      // Only filter folders by lifecycle at root level
      // Inside folders, show all subfolders regardless of phase
      // Phase filtering now handled client-side

      const response = await api.get('/folders', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data.success) {
        setFolders(response.data.folders);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchBreadcrumbs = async (folderId) => {
    if (!folderId) {
      setBreadcrumbs([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/folders/${folderId}/breadcrumb`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setBreadcrumbs(response.data.breadcrumb);
        setCurrentFolderPhase(response.data.phase);
      }
    } catch (error) {
      console.error('Error fetching breadcrumbs:', error);
    }
  };

  // Navigate into a folder
  const navigateToFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderPhase(folder.phase);
    fetchBreadcrumbs(folder.id);
    setSearchQuery(''); // Clear search when navigating
  };

  // Navigate to root
  const navigateToRoot = () => {
    setCurrentFolderId(null);
    setBreadcrumbs([]);
    setCurrentFolderPhase(null);
  };

  // Navigate to a specific breadcrumb
  const navigateToBreadcrumb = (item, index) => {
    setCurrentFolderId(item.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  };

  // No client-side filtering needed as backend handles it
  const displayFiles = files;
  const displayFolders = folders;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      fetchFiles(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchData();
    toast.success('Documents uploaded successfully');
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.delete(`/files/company/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        toast.success('Document deleted successfully');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete document');
    }
    setOpenMenuId(null);
  };

  const handleViewFile = (file) => {
    setSelectedFile(file);
    setShowPreviewModal(true);
    setOpenMenuId(null);
  };

  const handleEditFile = (file) => {
    setSelectedFile(file);
    setShowEditModal(true);
    setOpenMenuId(null);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedFile(null);
    fetchData();
    toast.success('Document updated successfully');
  };

  const handleDownloadFile = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/files/company/${file.id}/signed-url`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const link = document.createElement('a');
        link.href = response.data.signedUrl;
        link.download = file.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download document');
    }
    setOpenMenuId(null);
  };

  const handleHistoryView = (file) => {
    setHistoryFile(file);
    setShowHistorySidebar(true);
    setOpenMenuId(null);
  };

  const handleFolderCreated = () => {
    setShowCreateFolderModal(false);
    fetchData();
  };

  const handleToggleAI = async (file) => {
    try {
      setProcessingAiFileIds(prev => new Set(prev).add(file.id));
      const token = localStorage.getItem('token');
      const response = await api.patch(`/files/ai/toggle/${file.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(file.ai_indexed ? 'Removed from AI search' : 'Document indexed for AI search');
        fetchData();
      }
    } catch (error) {
      console.error('Error toggling AI index:', error);
      toast.error(error.response?.data?.message || 'Failed to update AI indexing');
    } finally {
      setProcessingAiFileIds(prev => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
    setOpenMenuId(null);
  };

  const handleMoveFile = (file) => {
    setSelectedFile(file);
    setMoveMode('move');
    setShowMoveModal(true);
    setOpenMenuId(null);
  };

  const handleAddToCollection = (file) => {
    setCollectionFile(file);
    setShowCollectionModal(true);
    setOpenMenuId(null);
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Are you sure you want to delete "${folder.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await api.delete(`/folders/${folder.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        toast.success('Folder deleted');
        fetchData();
      }
    } catch (error) {
      if (error.response?.data?.hasSubfolders || error.response?.data?.hasFiles) {
        const proceed = window.confirm(
          `This folder contains ${error.response.data.subfolderCount || 0} subfolders and ${error.response.data.fileCount || 0} documents. Delete anyway?`
        );
        if (proceed) {
          try {
            await api.delete(`/folders/${folder.id}?force=true`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            toast.success('Folder and contents deleted');
            fetchData();
          } catch (err) {
            toast.error('Failed to delete folder');
          }
        }
      } else {
        toast.error('Failed to delete folder');
      }
    }
    setOpenFolderMenuId(null);
  };

  const handleRenameFolder = async (folder) => {
    const newName = window.prompt('Enter new folder name:', folder.name);
    if (newName && newName !== folder.name) {
      try {
        const token = localStorage.getItem('token');
        await api.put(`/folders/${folder.id}`, { name: newName }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Folder renamed');
        fetchData();
      } catch (error) {
        toast.error('Failed to rename folder');
      }
    }
    setOpenFolderMenuId(null);
  };

  // ─── Drag-and-Drop Handlers ───
  const handleDragStart = (e, file) => {
    setDraggedFile(file);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', file.id);
    // Add a slight delay so the drag image shows correctly
    setTimeout(() => { e.target.style.opacity = '0.5'; }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedFile(null);
    setDropTargetFolderId(null);
  };

  const handleFolderDragOver = (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dropTargetFolderId !== folderId) {
      setDropTargetFolderId(folderId);
    }
  };

  const handleFolderDragLeave = (e) => {
    e.preventDefault();
    // Only clear if we're actually leaving the folder element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetFolderId(null);
    }
  };

  const handleFolderDrop = async (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetFolderId(null);

    // Handle external file drops (from desktop)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && !draggedFile) {
      setIsDraggingExternal(false);
      // Open upload modal for external files
      if (folder) {
        navigateToFolder(folder);
      }
      setShowUploadModal(true);
      return;
    }

    if (!draggedFile) return;
    // Don't move if dropping on the same folder the file is already in
    if (draggedFile.folder_id === folder.id) {
      setDraggedFile(null);
      toast.error('File is already in this folder');
      return;
    }

    try {
      const response = await api.post(`/folders/files/${draggedFile.id}/move`, {
        folder_id: folder.id
      });
      if (response.data.file) {
        toast.success(`Moved "${draggedFile.file_name}" to "${folder.name}"`);
        fetchData();
      }
    } catch (error) {
      console.error('Error moving file:', error);
      toast.error(error.response?.data?.message || 'Failed to move file');
    }
    setDraggedFile(null);
  };

  // Handle page-level drag for external file uploads
  const handlePageDragOver = (e) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files') && !draggedFile) {
      setIsDraggingExternal(true);
    }
  };

  const handlePageDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingExternal(false);
    }
  };

  const handlePageDrop = (e) => {
    e.preventDefault();
    if (isDraggingExternal && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setIsDraggingExternal(false);
      setShowUploadModal(true);
    }
    setIsDraggingExternal(false);
  };

  // Get folder lifecycle badge
  const getFolderLifecycleBadge = (phase) => {
    switch (phase) {
      case 'preboarding':
        return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Preboarding' };
      case 'onboarding':
        return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Onboarding' };
      case 'both':
        return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Both' };
      default:
        return { bg: 'bg-muted', text: 'text-muted-foreground', label: phase || 'Unknown' };
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get file icon + color based on type
  const getFileIcon = (fileType) => {
    if (!fileType) return { icon: File, color: TOKENS.defaultFileColor, bg: '#5F636812' };
    if (fileType.includes('pdf')) return { icon: FileText, color: TOKENS.pdfColor, bg: '#EA433512' };
    if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv')) return { icon: FileSpreadsheet, color: TOKENS.spreadsheetColor, bg: '#34A85312' };
    if (fileType.includes('image')) return { icon: FileImage, color: TOKENS.imageColor, bg: '#4285F412' };
    if (fileType.includes('word') || fileType.includes('document')) return { icon: FileText, color: TOKENS.docColor, bg: '#4285F412' };
    return { icon: File, color: TOKENS.defaultFileColor, bg: '#5F636812' };
  };

  // Get lifecycle badge styling
  const getLifecycleBadge = (phase) => {
    switch (phase) {
      case 'preboarding':
        return { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Preboarding' };
      case 'onboarding':
        return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Onboarding' };
      case 'both':
        return { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Both' };
      default:
        return { bg: 'bg-muted', text: 'text-muted-foreground', label: phase || 'Unknown' };
    }
  };

  // Get audience badge styling and label
  const getAudienceBadge = (file) => {
    const category = file.file_category;
    switch (category) {
      case 'company-wide':
        return { icon: Users, bg: 'bg-muted', text: 'text-muted-foreground', label: 'Everyone' };
      case 'role-specific':
        return { icon: Briefcase, bg: 'bg-muted', text: 'text-muted-foreground', label: `By Role: ${file.role_specific || 'N/A'}` };
      case 'employee-specific':
        const employees = file.target_employees;
        const names = employees ? employees.map(e => e.name).join(', ') : 'N/A';
        return { icon: User, bg: 'bg-muted', text: 'text-muted-foreground', label: `Individual: ${names.length > 30 ? names.substring(0, 30) + '...' : names}` };
      case 'private':
        return { icon: Shield, bg: 'bg-muted', text: 'text-muted-foreground', label: 'Admin Only' };
      default:
        return { icon: Users, bg: 'bg-muted', text: 'text-muted-foreground', label: category || 'Unknown' };
    }
  };

  // Get review date styling (color-coded)
  const getReviewDateStyle = (reviewDate) => {
    if (!reviewDate) return { text: '-', className: 'text-muted-foreground' };
    const now = new Date();
    const review = new Date(reviewDate);
    const diffDays = Math.ceil((review - now) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      return { text: formatDate(reviewDate), className: 'text-destructive font-medium' };
    } else if (diffDays <= 14) {
      return { text: formatDate(reviewDate), className: 'text-amber-600 font-medium' };
    } else {
      return { text: formatDate(reviewDate), className: 'text-muted-foreground' };
    }
  };

  // ─── File context menu (shared by grid + list) ───
  const renderFileMenu = (file) => {
    const menuBtn = (icon, label, onClick, danger) => (
      <button
        key={label}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
          padding: '7px 14px', border: 'none', background: 'none', textAlign: 'left',
          fontSize: '13px', color: danger ? '#DC2626' : TOKENS.textPrimary,
          cursor: 'pointer', borderRadius: '4px',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = danger ? '#FEE2E2' : TOKENS.bgHover; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
      >
        {icon}
        {label}
      </button>
    );

    return (
      <div style={{
        position: 'absolute', right: 0, top: '100%', marginTop: '4px', minWidth: '180px',
        background: TOKENS.bgCard, border: `1px solid ${TOKENS.border}`, borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '4px', zIndex: 50,
      }}>
        {menuBtn(<Eye size={14} style={{ color: TOKENS.textSecondary }} />, 'Preview', () => handleViewFile(file))}
        {menuBtn(<Edit3 size={14} style={{ color: TOKENS.textSecondary }} />, 'Edit Details', () => handleEditFile(file))}
        {menuBtn(<Download size={14} style={{ color: TOKENS.textSecondary }} />, 'Download', () => handleDownloadFile(file))}
        {menuBtn(<Move size={14} style={{ color: TOKENS.textSecondary }} />, 'Move', () => handleMoveFile(file))}
        {menuBtn(<Library size={14} style={{ color: TOKENS.textSecondary }} />, 'Add to Collection', () => handleAddToCollection(file))}
        <div style={{ height: '1px', background: TOKENS.border, margin: '4px 0' }} />
        {menuBtn(
          <Sparkles size={14} style={{ color: '#7C3AED' }} />,
          file.ai_indexed ? 'Remove from AI' : 'Add to AI Index',
          () => handleToggleAI(file)
        )}
        {menuBtn(<History size={14} style={{ color: TOKENS.textSecondary }} />, 'Version History', () => handleHistoryView(file))}
        {menuBtn(<Info size={14} style={{ color: TOKENS.textSecondary }} />, 'Compliance', () => { setComplianceFile(file); setShowComplianceModal(true); setOpenMenuId(null); })}
        <div style={{ height: '1px', background: TOKENS.border, margin: '4px 0' }} />
        {menuBtn(<Trash2 size={14} />, 'Delete', () => handleDeleteFile(file.id), true)}
      </div>
    );
  };


  const isInsideFolder = currentFolderId !== null;

  return (
    <div style={{ background: TOKENS.bgPage, minHeight: '100vh', position: 'relative' }} onDragOver={handlePageDragOver} onDragLeave={handlePageDragLeave} onDrop={handlePageDrop}>
      {/* Upload Drop Zone Overlay */}
      {isDraggingExternal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ background: TOKENS.bgCard, border: `3px dashed ${TOKENS.accent}`, borderRadius: '16px', padding: '60px 80px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <Upload size={48} style={{ color: TOKENS.accent, marginBottom: '16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: TOKENS.textPrimary, marginBottom: '8px' }}>Drop files to upload</h3>
            <p style={{ fontSize: '14px', color: TOKENS.textSecondary }}>Files will be uploaded to the current folder</p>
          </div>
        </div>
      )}
      <div style={{ maxWidth: showAIPanel ? '1600px' : '1200px', margin: '0 auto', padding: '24px 32px', transition: 'max-width 0.3s' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: 700, color: TOKENS.textPrimary, margin: 0 }}>Documents</h1>
                <p style={{ fontSize: '14px', color: TOKENS.textSecondary, marginTop: '4px' }}>
                  Manage all company documents across lifecycle stages
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowAIPanel(!showAIPanel)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    height: TOKENS.btnHeight, padding: TOKENS.btnPadding,
                    fontSize: '13px', fontWeight: 600,
                    background: showAIPanel ? '#7C3AED15' : TOKENS.bgCard,
                    color: showAIPanel ? '#7C3AED' : TOKENS.textPrimary,
                    border: `1px solid ${showAIPanel ? '#7C3AED40' : TOKENS.border}`,
                    borderRadius: TOKENS.btnRadius, cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <Sparkles size={14} /> Ask AI
                </button>
                <button
                  onClick={() => setShowCreateFolderModal(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    height: TOKENS.btnHeight, padding: TOKENS.btnPadding,
                    fontSize: '13px', fontWeight: 600,
                    background: TOKENS.bgCard, color: TOKENS.textPrimary,
                    border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.btnRadius,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.bgCard; }}
                >
                  <FolderPlus size={14} /> New Folder
                </button>
                <button
                  onClick={() => setShowUploadModal(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    height: TOKENS.btnHeight, padding: TOKENS.btnPadding,
                    fontSize: '13px', fontWeight: 600,
                    background: TOKENS.accent, color: '#fff',
                    border: 'none', borderRadius: TOKENS.btnRadius,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.accentHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.accent; }}
                >
                  <Upload size={14} /> Upload
                </button>
              </div>
            </div>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€ Breadcrumb â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {(isInsideFolder || breadcrumbs.length > 0) && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '8px 0', marginBottom: '8px', fontSize: '14px',
              }}>
                <button
                  onClick={navigateToRoot}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'none', border: 'none', borderRadius: '4px', color: TOKENS.textSecondary, cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; e.currentTarget.style.color = TOKENS.textPrimary; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = TOKENS.textSecondary; }}
                >
                  <Home size={15} /> My Documents
                </button>
                {breadcrumbs.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <ChevronRight size={14} style={{ color: TOKENS.textSecondary, flexShrink: 0 }} />
                    <button
                      onClick={() => navigateToBreadcrumb(item, index)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px',
                        background: 'none', border: 'none', borderRadius: '4px',
                        color: index === breadcrumbs.length - 1 ? TOKENS.textPrimary : TOKENS.textSecondary,
                        fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                        cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                    >
                      {item.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€ Toolbar: Search + Filters + View Toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '10px 16px', background: TOKENS.bgCard,
              border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.cardRadius,
              marginBottom: '20px', flexWrap: 'wrap',
            }}>
              {/* Back Button (inside folder) */}
              {isInsideFolder && (
                <button
                  onClick={() => {
                    if (breadcrumbs.length > 1) {
                      navigateToBreadcrumb(breadcrumbs[breadcrumbs.length - 2], breadcrumbs.length - 2);
                    } else {
                      navigateToRoot();
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', padding: '6px', background: 'none', border: `1px solid ${TOKENS.border}`, borderRadius: '6px', color: TOKENS.textSecondary, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                  title="Go back"
                >
                  <ArrowLeft size={16} />
                </button>
              )}

              {/* Search */}
              <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '320px' }}>
                <Search size={15} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: TOKENS.textSecondary }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isInsideFolder ? "Search in this folder..." : "Search documents..."}
                  style={{
                    width: '100%', height: TOKENS.filterHeight, paddingLeft: '32px', paddingRight: searchQuery ? '28px' : '12px',
                    fontSize: '13px', border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.filterRadius,
                    background: TOKENS.bgPage, color: TOKENS.textPrimary, outline: 'none', transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = TOKENS.accent; }}
                  onBlur={(e) => { e.target.style.borderColor = TOKENS.border; }}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: TOKENS.textSecondary }}>
                    <X size={14} />
                  </button>
                )}
              </div>

              <div style={{ width: '1px', height: '20px', background: TOKENS.border }} />

              {/* Lifecycle Filters (only at root) */}
              {!isInsideFolder && (
                <div style={{ display: 'flex', gap: '4px' }}>
                  {lifecycleOptions.map((option) => {
                    const isActive = lifecycleFilter === option.id;
                    return (
                      <button
                        key={option.id}
                        onClick={() => setLifecycleFilter(option.id)}
                        style={{
                          height: TOKENS.filterHeight, padding: '6px 14px',
                          fontSize: '13px', fontWeight: isActive ? 600 : 400,
                          background: isActive ? (option.id === 'all' ? TOKENS.accent : option.id === 'preboarding' ? '#F59E0B15' : option.id === 'onboarding' ? '#3B82F615' : '#8B5CF615') : 'transparent',
                          color: isActive ? (option.id === 'all' ? '#fff' : option.id === 'preboarding' ? '#B45309' : option.id === 'onboarding' ? '#1D4ED8' : '#6D28D9') : TOKENS.textSecondary,
                          border: `1px solid ${isActive ? 'transparent' : TOKENS.border}`,
                          borderRadius: TOKENS.filterRadius, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Current folder phase badge */}
              {isInsideFolder && currentFolderPhase && (
                <span style={{
                  fontSize: '12px', fontWeight: 600, padding: '4px 10px', borderRadius: '12px',
                  background: currentFolderPhase === 'preboarding' ? '#F59E0B15' : currentFolderPhase === 'onboarding' ? '#3B82F615' : '#8B5CF615',
                  color: currentFolderPhase === 'preboarding' ? '#B45309' : currentFolderPhase === 'onboarding' ? '#1D4ED8' : '#6D28D9',
                }}>
                  {currentFolderPhase === 'preboarding' ? 'Preboarding' : currentFolderPhase === 'onboarding' ? 'Onboarding' : 'Both'}
                </span>
              )}

              {/* Audience Filter Dropdown */}
              <select
                value={audienceFilter}
                onChange={(e) => setAudienceFilter(e.target.value)}
                style={{
                  height: TOKENS.filterHeight, padding: TOKENS.filterPadding,
                  fontSize: '13px', border: `1px solid ${TOKENS.border}`,
                  borderRadius: TOKENS.filterRadius, background: TOKENS.bgCard,
                  color: TOKENS.textPrimary, cursor: 'pointer', outline: 'none',
                }}
              >
                {audienceOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>

              {/* Review Status Toggle */}
              <button
                onClick={() => {
                  const newStatus = reviewStatusFilter === 'all' ? 'needs_review' : 'all';
                  setReviewStatusFilter(newStatus);
                  if (newStatus !== 'all') { setSearchParams({ review_status: newStatus }); } else { setSearchParams({}); }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  height: TOKENS.filterHeight, padding: TOKENS.filterPadding,
                  fontSize: '13px', fontWeight: reviewStatusFilter !== 'all' ? 600 : 400,
                  background: reviewStatusFilter !== 'all' ? '#F59E0B15' : 'transparent',
                  color: reviewStatusFilter !== 'all' ? '#B45309' : TOKENS.textSecondary,
                  border: `1px solid ${reviewStatusFilter !== 'all' ? '#F59E0B40' : TOKENS.border}`,
                  borderRadius: TOKENS.filterRadius, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}
              >
                <Calendar size={13} />
                {reviewStatusFilter === 'all' ? 'Review Due' : reviewStatusFilter === 'needs_review' ? 'Needs Review' : reviewStatusFilter === 'overdue' ? 'Overdue' : 'Due Soon'}
              </button>

              <div style={{ flex: 1 }} />

              {/* View Mode Toggle */}
              <div style={{ display: 'flex', border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.filterRadius, overflow: 'hidden' }}>
                <button
                  onClick={() => setViewMode('grid')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '34px', height: '32px', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                    background: viewMode === 'grid' ? TOKENS.accent : 'transparent',
                    color: viewMode === 'grid' ? '#fff' : TOKENS.textSecondary,
                  }}
                  title="Grid view"
                >
                  <Grid3X3 size={15} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '34px', height: '32px', border: 'none', borderLeft: `1px solid ${TOKENS.border}`, cursor: 'pointer', transition: 'all 0.15s',
                    background: viewMode === 'list' ? TOKENS.accent : 'transparent',
                    color: viewMode === 'list' ? '#fff' : TOKENS.textSecondary,
                  }}
                  title="List view"
                >
                  <List size={15} />
                </button>
              </div>

              {/* Refresh */}
              <button
                onClick={() => fetchData()}
                style={{ display: 'flex', alignItems: 'center', padding: '6px', background: 'none', border: `1px solid ${TOKENS.border}`, borderRadius: '6px', color: TOKENS.textSecondary, cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                title="Refresh"
              >
                <RefreshCw size={15} />
              </button>
            </div>

            {/* Review Status Active Banner */}
            {reviewStatusFilter !== 'all' && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', marginBottom: '16px', borderRadius: TOKENS.cardRadius,
                background: '#FEF3C7', border: '1px solid #FDE68A',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertTriangle size={16} style={{ color: '#D97706' }} />
                  <span style={{ fontSize: '13px', fontWeight: 500, color: '#92400E' }}>
                    {reviewStatusFilter === 'needs_review' && 'Showing documents that need review (due within 14 days or overdue)'}
                    {reviewStatusFilter === 'overdue' && 'Showing overdue documents only'}
                    {reviewStatusFilter === 'approaching' && 'Showing documents due within 14 days'}
                  </span>
                </div>
                <button
                  onClick={() => { setReviewStatusFilter('all'); setSearchParams({}); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#92400E', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                >
                  Clear filter <X size={14} />
                </button>
              </div>
            )}

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€ Content Area â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '80px 0', color: TOKENS.textSecondary }}>
                <div style={{ width: '40px', height: '40px', border: `3px solid ${TOKENS.border}`, borderTop: `3px solid ${TOKENS.accent}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ fontSize: '14px' }}>Loading documents...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <div>
                {/* â”€â”€â”€ Folders Section â”€â”€â”€ */}
                {displayFolders.length > 0 && !searchQuery && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${TOKENS.border}` }}>
                      <FolderOpen size={16} style={{ color: TOKENS.accent }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: TOKENS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Folders ({displayFolders.length})
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(180px, 1fr))' : '1fr', gap: viewMode === 'grid' ? '12px' : '4px' }}>
                      {displayFolders.map((folder) => {
                        const lifecycle = getFolderLifecycleBadge(folder.phase);
                        return (
                          <div
                            key={folder.id}
                            onClick={() => navigateToFolder(folder)}
                            onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                            onDragLeave={handleFolderDragLeave}
                            onDrop={(e) => handleFolderDrop(e, folder)}
                            style={{
                              position: 'relative', display: 'flex',
                              flexDirection: viewMode === 'grid' ? 'column' : 'row',
                              alignItems: viewMode === 'grid' ? 'center' : 'center',
                              gap: viewMode === 'grid' ? '8px' : '12px',
                              padding: viewMode === 'grid' ? '20px 16px 16px' : '10px 16px',
                              background: dropTargetFolderId === folder.id ? `${TOKENS.accent}15` : TOKENS.bgCard,
                              border: `2px ${dropTargetFolderId === folder.id ? 'dashed' : 'solid'} ${dropTargetFolderId === folder.id ? TOKENS.accent : TOKENS.border}`,
                              borderRadius: TOKENS.cardRadius, cursor: 'pointer',
                              transition: 'all 0.2s',
                              transform: dropTargetFolderId === folder.id ? 'scale(1.03)' : 'scale(1)',
                            }}
                            onMouseEnter={(e) => { if (!dropTargetFolderId) { e.currentTarget.style.borderColor = TOKENS.accent; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; } }}
                            onMouseLeave={(e) => { if (!dropTargetFolderId) { e.currentTarget.style.borderColor = TOKENS.border; e.currentTarget.style.boxShadow = 'none'; } }}
                          >
                            <div style={{
                              width: viewMode === 'grid' ? '48px' : '36px',
                              height: viewMode === 'grid' ? '48px' : '36px',
                              borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: (folder.color || '#5F6368') + '18',
                              flexShrink: 0,
                            }}>
                              <Folder size={viewMode === 'grid' ? 28 : 20} style={{ color: folder.color || '#5F6368' }} />
                            </div>
                            <div style={{ textAlign: viewMode === 'grid' ? 'center' : 'left', minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={folder.name}>{folder.name}</div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: viewMode === 'grid' ? 'center' : 'flex-start', marginTop: '4px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: lifecycle.bg === 'bg-amber-100' ? '#F59E0B15' : lifecycle.bg === 'bg-blue-100' ? '#3B82F615' : lifecycle.bg === 'bg-purple-100' ? '#8B5CF615' : TOKENS.bgHover, color: lifecycle.text === 'text-amber-700' ? '#B45309' : lifecycle.text === 'text-blue-700' ? '#1D4ED8' : lifecycle.text === 'text-purple-700' ? '#6D28D9' : TOKENS.textSecondary }}>{lifecycle.label}</span>
                                <span style={{ fontSize: '11px', color: TOKENS.textSecondary }}>{folder.file_count || 0} docs</span>
                              </div>
                            </div>
                            {/* Folder Actions */}
                            <div className="dropdown-menu" style={{ position: viewMode === 'grid' ? 'absolute' : 'relative', top: viewMode === 'grid' ? '8px' : 'auto', right: viewMode === 'grid' ? '8px' : '0' }}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setOpenFolderMenuId(openFolderMenuId === folder.id ? null : folder.id); }}
                                style={{ display: 'flex', alignItems: 'center', padding: '4px', background: 'none', border: 'none', borderRadius: '4px', color: TOKENS.textSecondary, cursor: 'pointer', opacity: 0.5, transition: 'opacity 0.15s' }}
                                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = TOKENS.bgHover; }}
                                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.background = 'none'; }}
                              >
                                <MoreVertical size={15} />
                              </button>
                              {openFolderMenuId === folder.id && (
                                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: '4px', width: '160px', background: TOKENS.bgCard, border: `1px solid ${TOKENS.border}`, borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, overflow: 'hidden' }}>
                                  {[
                                    { label: 'Open', icon: FolderOpen, action: (e) => { e.stopPropagation(); navigateToFolder(folder); } },
                                    { label: 'Rename', icon: Edit3, action: (e) => { e.stopPropagation(); handleRenameFolder(folder); } },
                                    { label: 'Delete', icon: Trash2, action: (e) => { e.stopPropagation(); handleDeleteFolder(folder); }, danger: true, divider: true },
                                  ].map((item, i) => (
                                    <React.Fragment key={i}>
                                      {item.divider && <div style={{ height: '1px', background: TOKENS.border }} />}
                                      <button
                                        onClick={item.action}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 14px', fontSize: '13px', background: 'none', border: 'none', color: item.danger ? '#DC2626' : TOKENS.textPrimary, cursor: 'pointer', textAlign: 'left', transition: 'background 0.1s' }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = item.danger ? '#FEE2E2' : TOKENS.bgHover; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                                      >
                                        <item.icon size={14} /> {item.label}
                                      </button>
                                    </React.Fragment>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* â”€â”€â”€ Files Section â”€â”€â”€ */}
                {displayFiles.length > 0 ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '8px', borderBottom: `1px solid ${TOKENS.border}` }}>
                      <FileText size={16} style={{ color: TOKENS.accent }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: TOKENS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Documents ({pagination.total})
                      </span>
                    </div>

                    {viewMode === 'grid' ? (
                      /* â”€â”€ Grid View â”€â”€ */
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                        {displayFiles.map((file) => {
                          const fileInfo = getFileIcon(file.file_type);
                          const FileIcon = fileInfo.icon;
                          return (
                            <div
                              key={file.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, file)}
                              onDragEnd={handleDragEnd}
                              style={{
                                position: 'relative', background: TOKENS.bgCard,
                                border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.cardRadius,
                                cursor: draggedFile ? 'grabbing' : 'pointer', transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.borderColor = TOKENS.accent; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.borderColor = TOKENS.border; e.currentTarget.style.boxShadow = 'none'; }}
                              onClick={() => handleViewFile(file)}
                            >
                              {/* File Icon Area */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', background: fileInfo.bg, position: 'relative', borderRadius: `${TOKENS.cardRadius} ${TOKENS.cardRadius} 0 0`, overflow: 'hidden' }}>
                                <FileIcon size={40} style={{ color: fileInfo.color }} />
                                {file.ai_indexed && (
                                  <div style={{ position: 'absolute', top: '8px', right: '8px', width: '20px', height: '20px', background: '#7C3AED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Sparkles size={10} style={{ color: '#fff' }} />
                                  </div>
                                )}
                                {processingAiFileIds.has(file.id) && (
                                  <Loader2 size={16} style={{ position: 'absolute', top: '8px', right: '8px', color: '#7C3AED', animation: 'spin 1s linear infinite' }} />
                                )}
                              </div>
                              {/* File Info */}
                              <div style={{ padding: '12px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: '4px' }} title={file.file_name}>{file.file_name}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: TOKENS.textSecondary }}>
                                  <span>{formatFileSize(file.file_size)}</span>
                                  <span>â€¢</span>
                                  <span>{formatDate(file.created_at)}</span>
                                </div>
                              </div>
                              {/* Actions */}
                              <div className="dropdown-menu" style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === file.id ? null : file.id); }}
                                  style={{ display: 'flex', alignItems: 'center', padding: '4px', background: 'rgba(255,255,255,0.8)', border: 'none', borderRadius: '4px', color: TOKENS.textSecondary, cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                                >
                                  <MoreVertical size={14} />
                                </button>
                                {openMenuId === file.id && renderFileMenu(file)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* â”€â”€ List View â”€â”€ */
                      <div style={{ background: TOKENS.bgCard, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.cardRadius }}>
                        {/* Table Header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 2fr 2fr 1fr 1fr 40px', gap: '12px', padding: '10px 16px', background: TOKENS.bgHover, fontSize: '11px', fontWeight: 700, color: TOKENS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          <div>Document</div>
                          <div>Lifecycle</div>
                          <div>Audience</div>
                          <div>{isInsideFolder ? 'Location' : 'Folder'}</div>
                          <div>Uploaded</div>
                          <div>Review By</div>
                          <div></div>
                        </div>
                        {/* Table Rows */}
                        {displayFiles.map((file) => {
                          const fileInfo = getFileIcon(file.file_type);
                          const FileIcon = fileInfo.icon;
                          const lifecycle = getLifecycleBadge(file.phase);
                          const audience = getAudienceBadge(file);
                          const AudienceIcon = audience.icon;
                          const reviewStyle = getReviewDateStyle(file.review_by_date);
                          return (
                            <div
                              key={file.id}
                              draggable
                              onDragStart={(e) => handleDragStart(e, file)}
                              onDragEnd={handleDragEnd}
                              style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 2fr 2fr 1fr 1fr 40px', gap: '12px', padding: '10px 16px', alignItems: 'center', borderTop: `1px solid ${TOKENS.border}`, transition: 'background 0.1s', cursor: draggedFile ? 'grabbing' : 'pointer' }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              onClick={() => handleViewFile(file)}
                            >
                              {/* Name */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: fileInfo.bg, flexShrink: 0, position: 'relative' }}>
                                  <FileIcon size={16} style={{ color: fileInfo.color }} />
                                  {file.ai_indexed && <div style={{ position: 'absolute', top: '-3px', right: '-3px', width: '12px', height: '12px', background: '#7C3AED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid white' }}><Sparkles size={6} style={{ color: '#fff' }} /></div>}
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: '13px', fontWeight: 500, color: TOKENS.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={file.file_name}>{file.file_name}</div>
                                  <div style={{ fontSize: '11px', color: TOKENS.textSecondary }}>{formatFileSize(file.file_size)}</div>
                                </div>
                              </div>
                              {/* Lifecycle */}
                              <div><span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: lifecycle.bg === 'bg-amber-100' ? '#F59E0B15' : lifecycle.bg === 'bg-blue-100' ? '#3B82F615' : '#8B5CF615', color: lifecycle.text === 'text-amber-700' ? '#B45309' : lifecycle.text === 'text-blue-700' ? '#1D4ED8' : '#6D28D9' }}>{lifecycle.label}</span></div>
                              {/* Audience */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: TOKENS.textSecondary, minWidth: 0 }}>
                                <AudienceIcon size={12} style={{ flexShrink: 0 }} />
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={audience.label}>{audience.label}</span>
                              </div>
                              {/* Folder */}
                              <div style={{ fontSize: '12px', color: TOKENS.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                {file.folder_name ? <><Folder size={12} /><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={file.folder_name}>{file.folder_name}</span></> : <span style={{ opacity: 0.5 }}>Root</span>}
                              </div>
                              {/* Uploaded */}
                              <div style={{ fontSize: '12px', color: TOKENS.textSecondary }}>{formatDate(file.created_at)}</div>
                              {/* Review By */}
                              <div style={{ fontSize: '12px', color: reviewStyle.className.includes('destructive') ? '#DC2626' : reviewStyle.className.includes('amber') ? '#D97706' : TOKENS.textSecondary, fontWeight: reviewStyle.className.includes('font-medium') ? 600 : 400 }}>{reviewStyle.text}</div>
                              {/* Actions */}
                              <div className="dropdown-menu" style={{ position: 'relative' }}>
                                <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === file.id ? null : file.id); }} style={{ display: 'flex', alignItems: 'center', padding: '4px', background: 'none', border: 'none', borderRadius: '4px', color: TOKENS.textSecondary, cursor: 'pointer' }}>
                                  <MoreVertical size={15} />
                                </button>
                                {openMenuId === file.id && renderFileMenu(file)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '16px', padding: '0 4px' }}>
                      <span style={{ fontSize: '13px', color: TOKENS.textSecondary }}>
                        Showing {files.length} of {pagination.total} document{pagination.total !== 1 ? 's' : ''}
                      </span>
                      {pagination.pages > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} style={{ display: 'flex', alignItems: 'center', padding: '6px', background: 'none', border: `1px solid ${TOKENS.border}`, borderRadius: '6px', cursor: pagination.page === 1 ? 'not-allowed' : 'pointer', opacity: pagination.page === 1 ? 0.4 : 1, color: TOKENS.textSecondary }}>
                            <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
                          </button>
                          <span style={{ fontSize: '13px', color: TOKENS.textPrimary, padding: '0 8px', fontWeight: 500 }}>
                            Page {pagination.page} of {pagination.pages}
                          </span>
                          <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages} style={{ display: 'flex', alignItems: 'center', padding: '6px', background: 'none', border: `1px solid ${TOKENS.border}`, borderRadius: '6px', cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer', opacity: pagination.page === pagination.pages ? 0.4 : 1, color: TOKENS.textSecondary }}>
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : folders.length === 0 ? (
                  /* â”€â”€ Empty State â”€â”€ */
                  <div style={{ textAlign: 'center', padding: '80px 40px', background: TOKENS.bgCard, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.cardRadius }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: `${TOKENS.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      {isInsideFolder ? <FolderOpen size={32} style={{ color: TOKENS.accent }} /> : <Upload size={32} style={{ color: TOKENS.accent }} />}
                    </div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: TOKENS.textPrimary, marginBottom: '8px' }}>
                      {isInsideFolder ? 'This folder is empty' : 'No documents found'}
                    </h3>
                    <p style={{ fontSize: '14px', color: TOKENS.textSecondary, marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' }}>
                      {searchQuery ? 'No documents match your search.' : isInsideFolder ? 'Upload files or create subfolders to organize your documents.' : 'Upload your first document or create a folder to get started.'}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <button onClick={() => setShowCreateFolderModal(true)} style={{ height: TOKENS.btnHeight, padding: TOKENS.btnPadding, fontSize: '13px', fontWeight: 600, background: TOKENS.bgCard, color: TOKENS.textPrimary, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.btnRadius, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FolderPlus size={14} /> Create Folder
                      </button>
                      <button onClick={() => setShowUploadModal(true)} style={{ height: TOKENS.btnHeight, padding: TOKENS.btnPadding, fontSize: '13px', fontWeight: 600, background: TOKENS.accent, color: '#fff', border: 'none', borderRadius: TOKENS.btnRadius, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Upload size={14} /> Upload Files
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* AI Panel */}
          <AdminDocumentsAIPanel isOpen={showAIPanel} onToggle={() => setShowAIPanel(!showAIPanel)} />
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <FileUploadModal
          phase={currentFolderPhase || "onboarding"}
          folderId={currentFolderId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
          showLifecycleSelector={true}
          files={files}
        />
      )}

      {showPreviewModal && selectedFile && (
        <FilePreviewModal
          file={selectedFile}
          onClose={() => { setShowPreviewModal(false); setSelectedFile(null); }}
        />
      )}

      {showEditModal && selectedFile && (
        <FileEditModal
          file={selectedFile}
          onClose={() => { setShowEditModal(false); setSelectedFile(null); }}
          onSuccess={handleEditSuccess}
        />
      )}

      {showCreateFolderModal && (
        <CreateFolderModal
          phase={currentFolderPhase || "onboarding"}
          parentId={currentFolderId}
          parentName={breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : null}
          onClose={() => setShowCreateFolderModal(false)}
          onSuccess={handleFolderCreated}
          showPhaseSelector={!isInsideFolder}
        />
      )}

      {showMoveModal && selectedFile && (
        <FileMoveModal
          file={selectedFile}
          mode={moveMode}
          phase={selectedFile?.phase || currentFolderPhase || 'onboarding'}
          onClose={() => { setShowMoveModal(false); setSelectedFile(null); }}
          onSuccess={() => { setShowMoveModal(false); setSelectedFile(null); fetchData(); }}
        />
      )}

      {showCollectionModal && collectionFile && (
        <AddToCollectionModal
          isOpen={showCollectionModal}
          file={collectionFile}
          onClose={() => { setShowCollectionModal(false); setCollectionFile(null); }}
          onSuccess={() => { setShowCollectionModal(false); setCollectionFile(null); toast.success('File added to collection'); }}
        />
      )}

      <FileHistorySidebar
        file={historyFile}
        isOpen={showHistorySidebar}
        onClose={() => setShowHistorySidebar(false)}
      />

      {showComplianceModal && complianceFile && (
        <ComplianceDetailsModal
          file={complianceFile}
          onClose={() => { setShowComplianceModal(false); setComplianceFile(null); }}
        />
      )}
    </div>
  );
};

export default AdminFilesPage;

